import React, { FC, useEffect, useLayoutEffect, useState } from "react"
import Loading from "../../../../ui/Loading"
import { KButton, KSpan } from "kahuna-base-react-components"
import { crawlers, users } from "../../../../../apis"
import { DrmSummary, UserLog } from "../../../../../types"
import { mapListToCamelCase } from "../../../../../utility"

import { connect } from "react-redux"
import { RootState } from "../../../../../store"
import { SetURLSearchParams } from "react-router-dom"
import Pagination from "../../../../ui/table/Pagination"
import AdminTakedownLog from "./AdminTakedownLog"
import AdminReleaseTakedownLog from "./AdminReleaseTakedownLog"

const columnWidths: any = {
  ID: "4%",
  DATE: "10%",
  ADVANCE_CATALOG: "9%",
  RELEASE_COUNT: "9%",
  FOUND_CONFLICT: "9%",
  OPEN_CONFLICT: "9%",
  IDLE_CONFLICT: "9%",
  NEW_CONFLICT: "9%",
  UNDER_REVIEW: "9%",
  ACTION_TAKEN: "9%",
  CLOSED: "9%",
  REMOVED: "9%",
  ACTION: "5%"
}

type UserLogMap = {
  [key: number]: UserLog[]
}

type AdminDrmLogProps = {
  params: URLSearchParams
  setParams: SetURLSearchParams
  headerDiv: JSX.Element
}

const AdminDrmLog: FC<AdminDrmLogProps> = (props) => {
  const [loading, setLoading] = useState(false)
  const [offset, setOffset] = useState(0)
  const [limit, setLimit] = useState(10)
  const [drmSummaryList, setDrmSummaryList] = useState<DrmSummary[]>([])
  const [count, setCount] = useState(0)
  const [selectedDrmSummary, setSelectedDrmSummary] = useState<DrmSummary>()
  const [selectedView, setSelectedView] = useState<"catalog" | "release">("release")
  const [openLogsMap, setOpenLogsMap] = useState<UserLogMap>({})

  const scrollPosRef = React.useRef(0)

  useLayoutEffect(() => {
    if (!loading && scrollPosRef.current > 0) {
      window.scrollTo({
        top: scrollPosRef.current,
        behavior: "auto"
      })
    }
  }, [drmSummaryList])

  const fetchDrmSummaryList = (offset: number) => {
    setLoading(true)
    crawlers(`/takedown-logs/drm_summaries/?offset=${offset}&limit=${limit}`, "GET").then((resp) => {
      setDrmSummaryList(mapListToCamelCase(resp.data.results))
      setCount(resp.data.count)
      setLoading(false)
    })
  }

  useEffect(() => {
    fetchDrmSummaryList(0)
  }, [])

  const onClickLog = async (drmSummary?: DrmSummary) => {
    if (!drmSummary) {
      return
    }

    if (drmSummary.id in openLogsMap) {
      delete openLogsMap[drmSummary.id]
    } else {
      const resp = await users(
        `/user_log/?type=drm_log_action&secondary_model=DrmSummary&secondary_id=${drmSummary.id}&option=fill_release_model`,
        "GET"
      )

      const userLogList = mapListToCamelCase(resp.data.results)
      openLogsMap[drmSummary.id] = userLogList
    }

    setOpenLogsMap({ ...openLogsMap })
  }

  const renderDrmSummaries = () => {
    return (
      <div className="pt-4 pb-4" style={{ display: "flex", flexDirection: "column" }}>
        <div className="mb-4 flex flex-col">
          <KSpan text="x / y represents two types of actions: ongoing actions and user logs." />
          <KSpan text="x — Ongoing actions carried over from the previous DRM crawl, unless removed." />
          <KSpan text="y — User actions performed during the current DRM crawl." />
        </div>
        <div className="flex pb-2 gap-2" style={{ width: "100%" }}>
          {Object.keys(columnWidths).map((header) => {
            return (
              <div key={`header_${header}`} style={{ width: columnWidths[header] }}>
                <KSpan fontWeight={500} text={header.replaceAll("_", " ")} />
              </div>
            )
          })}
        </div>
        {drmSummaryList.map((drmSummary) => {
          return (
            <div
              key={`drm_summary_${drmSummary.id}`}
              data-testid="admin-drm-summary-row"
              style={{ width: "100%" }}
              className="cursor-pointer"
              onClick={() => setSelectedDrmSummary(drmSummary)}
            >
              <div className="flex mt-3 pt-3 gap-2" style={{ width: "100%", borderTop: "1px dashed grey" }}>
                <div style={{ width: columnWidths.ID }} className="flex gap-1 items-center">
                  <KSpan text={drmSummary.id?.toString() || "-"} />
                </div>
                <div style={{ width: columnWidths.DATE }} className="flex gap-1 items-center">
                  <KSpan text={`${drmSummary.date}`} />
                </div>

                <div style={{ width: columnWidths.ADVANCE_CATALOG }} className="flex gap-1 items-center">
                  <KSpan text={drmSummary.advanceCatalogCount?.toString() || "-"} />
                </div>
                <div style={{ width: columnWidths.RELEASE_COUNT }} className="flex gap-1 items-center">
                  <KSpan text={drmSummary.releaseCount?.toString() || "-"} />
                </div>
                <div style={{ width: columnWidths.FOUND_CONFLICT }} className="flex gap-1 items-center">
                  <KSpan text={drmSummary.foundConflictCount?.toString() || "-"} />
                </div>
                <div style={{ width: columnWidths.OPEN_CONFLICT }} className="flex gap-1 items-center">
                  <KSpan text={drmSummary.openConflictCount?.toString() || "-"} />
                </div>
                <div style={{ width: columnWidths.IDLE_CONFLICT }} className="flex gap-1 items-center">
                  <KSpan text={drmSummary.idleConflictCount?.toString() || "-"} />
                  <KSpan text={"/"} />
                  <KSpan text={drmSummary.userLogCounts?.idle?.toString() || "-"} />
                </div>
                <div style={{ width: columnWidths.NEW_CONFLICT }} className="flex gap-1 items-center">
                  <KSpan text={drmSummary.newConflictCount?.toString() || "-"} />
                  <KSpan text={"/"} />
                  <KSpan text={drmSummary.userLogCounts?.new?.toString() || "-"} />
                </div>
                <div style={{ width: columnWidths.UNDER_REVIEW }} className="flex gap-1 items-center">
                  <KSpan text={drmSummary.underReviewCount?.toString() || "-"} />
                  <KSpan text={"/"} />
                  <KSpan text={drmSummary.userLogCounts?.underReview?.toString() || "-"} />
                </div>
                <div style={{ width: columnWidths.ACTION_TAKEN }} className="flex gap-1 items-center">
                  <KSpan text={drmSummary.actionTakenCount?.toString() || "-"} />
                  <KSpan text={"/"} />
                  <KSpan text={drmSummary.userLogCounts?.actionTaken?.toString() || "-"} />
                </div>
                <div style={{ width: columnWidths.CLOSED }} className="flex gap-1 items-center">
                  <KSpan text={drmSummary.closedCount?.toString() || "-"} />
                  <KSpan text={"/"} />
                  <KSpan text={drmSummary.userLogCounts?.closed?.toString() || "-"} />
                </div>
                <div style={{ width: columnWidths.REMOVED }} className="flex gap-1 items-center">
                  <KSpan text={drmSummary.removedCount?.toString() || "-"} />
                  <KSpan text={"/"} />
                  <KSpan text={drmSummary.userLogCounts?.removed?.toString() || "-"} />
                </div>
                <div style={{ width: columnWidths.ACTION }} className="flex gap-1 items-center">
                  <div
                    onClick={(e) => {
                      e.stopPropagation()
                      onClickLog(drmSummary)
                    }}
                  >
                    <KButton background="transparent" text="Logs" height="28px" width="54px" onClick={() => {}} />
                  </div>
                </div>
              </div>
              {drmSummary.id in openLogsMap && (
                <div
                  className="flex flex-col  gap-4 mb-2 overflow-x-auto thin-scrollbar pb-2 pt-2"
                  style={{ width: "140vh" }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {openLogsMap[drmSummary.id].length > 0 ? (
                    openLogsMap[drmSummary.id].map((userLog: UserLog) => {
                      return (
                        <div key={`track_log_${userLog.id}`} className="flex gap-2 flex-shrink-0 mt-1 ml-2">
                          <div className="flex flex-col">
                            <div className="flex flex-row gap-1">
                              <KSpan text={`${userLog.user?.firstName} ${userLog.user?.lastName}:`} fontWeight={500} />
                              <KSpan text={`${userLog.log || "-"}`} fontWeight={500} />
                            </div>
                            <div className="flex gap-10">
                              <KSpan
                                fontSize={11}
                                text={`Created: ${userLog.created?.substring(0, 19)}`}
                                fontWeight={500}
                              />
                              <KSpan fontSize={11} text={`Status: ${userLog.status}`} fontWeight={500} />
                              <KSpan fontSize={11} text={`Release UPC: ${userLog.release?.upc}`} fontWeight={500} />
                              <KSpan fontSize={11} text={`Release Title: ${userLog.release?.title}`} fontWeight={500} />
                            </div>
                          </div>
                        </div>
                      )
                    })
                  ) : (
                    <div className="ml-2">
                      <KSpan text="No Logs Available" />
                    </div>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>
    )
  }

  const onClickPagination = (pageNumber: number) => {
    setOffset((pageNumber - 1) * limit)
    fetchDrmSummaryList((pageNumber - 1) * limit)
  }

  return (
    <div className="catalog flex flex-col bg-[#fff] px-8 pt-4">
      {loading && <Loading />}
      <div className="flex justify-between items-center mb-6">
        {props.headerDiv}
        {selectedDrmSummary ? (
          <div className="flex gap-4">
            <div style={{ width: "200px", display: "flex", alignItems: "center", gap: "5px" }}>
              <KSpan text={`Selected Date: ${selectedDrmSummary.date}`} />
            </div>
            <KButton
              width="200px"
              height="35px"
              text="Back to DRM Logs"
              onClick={() => setSelectedDrmSummary(undefined)}
            />
          </div>
        ) : (
          <div className="flex gap-4">
            <div className="flex items-center gap-2">
              <KSpan text="View:" />
              <KButton
                text="Catalog"
                height="35px"
                onClick={() => setSelectedView("catalog")}
                background={selectedView === "catalog" ? "black" : "white"}
                textColor={selectedView === "catalog" ? "white" : "black"}
              />
              <KButton
                text="Release"
                height="35px"
                onClick={() => setSelectedView("release")}
                background={selectedView === "release" ? "black" : "white"}
                textColor={selectedView === "release" ? "white" : "black"}
              />
            </div>
          </div>
        )}
      </div>
      {selectedDrmSummary ? (
        selectedView === "catalog" ? (
          <AdminTakedownLog
            drmSummary={selectedDrmSummary}
            params={props.params}
            setParams={props.setParams}
            headerDiv={<div></div>}
          />
        ) : (
          <AdminReleaseTakedownLog drmSummary={selectedDrmSummary} isDrm={true} />
        )
      ) : (
        renderDrmSummaries()
      )}
      {!selectedDrmSummary && (
        <div>
          <Pagination
            key={""}
            hidden={false}
            size={"sm"}
            pageCount={Math.ceil(count / limit)}
            currentPage={offset / limit + 1}
            onClickPage={onClickPagination}
          />
        </div>
      )}
    </div>
  )
}

const mapStateToProps = (state: RootState) => {
  return {
    user: state.auth.user
  }
}

export default connect(mapStateToProps, {})(AdminDrmLog)
